import { observer } from "mobx-react-lite";
import { AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import { useESGDataEntryStore } from "@/features/esg/hooks/useESGReportStore";
import { ArrowRight } from "lucide-react";

const DataEntryPrefillModal = observer(() => {
  const { t } = useTran();
  const {
    showPrefillModal,
    setShowPrefillModal,
    prefillSuggestions,
    applyPrefillSuggestions,
  } = useESGDataEntryStore();

  const formatEntryValue = (entry) => {
    const parts = [];
    if (entry.value) parts.push(entry.value);
    if (entry.unit) parts.push(entry.unit);
    if (entry.valueUnit) parts.push(`(${entry.valueUnit})`);
    return parts.join(" ");
  };

  return (
    <AppDialog
      open={showPrefillModal}
      onOpenChange={setShowPrefillModal}
      title={t("Prefill Details")}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => setShowPrefillModal(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={applyPrefillSuggestions}>
            {t("Apply All Values")}
          </Button>
        </>
      }
    >
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        <p className="text-sm text-muted-foreground">
          {t(
            "The following data will be auto-filled from matching disclosures:",
          )}
        </p>

        <div className="space-y-3">
          {prefillSuggestions.map((match, index) => (
            <div
              key={index}
              className="border border-border rounded-radius p-3 space-y-2"
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline">{match.sourceFramework}</Badge>
                <span className="text-sm font-medium">{match.sourceCode}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{match.targetCode}</span>
              </div>

              <div className="text-xs text-muted-foreground">
                {t("From")}: {match.sourceReportName}
              </div>

              {match.entries?.slice(0, 2).map((entry, i) => (
                <div key={i} className="text-sm bg-muted/50 rounded px-2 py-1">
                  {formatEntryValue(entry)}
                </div>
              ))}

              {match.entries?.length > 2 && (
                <div className="text-xs text-muted-foreground">
                  {t("+ {{count}} more entries", {
                    count: match.entries.length - 2,
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AppDialog>
  );
});

export default DataEntryPrefillModal;
